import { useCallback, useMemo } from "react";
import { useDebounce } from "use-debounce";

import { useRosterManagementListMyOrganizationsPlayersQuery } from "@/generated/graphql";
import { normalizeForSearch } from "@/utils/normalizeForSearch";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const SEARCH_PAGE_SIZE = 20;

/**
 * Return type for usePlayerUuidLookup hook
 * @property getPlayerUuidByName - Function to lookup player UUID by name string
 * @property getPlayerUuidFromMarkdown - Function to resolve player UUID from markdown content and sequential ID
 * @property isLoading - Whether player data is currently being loaded
 * @property error - Error from player data query, null if no error
 */
interface UsePlayerUuidLookupReturn {
  getPlayerUuidByName: (playerName: string) => string | null;
  getPlayerUuidFromMarkdown: (
    markdownContent: string,
    sequentialId: string
  ) => string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Custom hook for converting player names to UUIDs using server-side search
 * Debounces the search term before querying the server to reduce API calls
 * Used for enabling clickable player links in chat that navigate to player detail pages
 * @param searchTerm - Player name or partial name to search for on the server
 */

export const usePlayerUuidLookup = (
  searchTerm = ""
): UsePlayerUuidLookupReturn => {
  const [debouncedSearch] = useDebounce(searchTerm, DEBOUNCE_MS);

  const trimmedSearch = debouncedSearch.trim();
  const shouldSkip = trimmedSearch.length < MIN_QUERY_LENGTH;

  const { data, loading, error } =
    useRosterManagementListMyOrganizationsPlayersQuery({
      variables: {
        first: SEARCH_PAGE_SIZE,
        search: trimmedSearch || undefined,
      },
      skip: shouldSkip,
      errorPolicy: "all",
    });

  const players = useMemo(() => {
    if (shouldSkip) {
      return [];
    }

    return (data?.listMyOrganizationsPlayers?.edges ?? [])
      .map(edge => edge?.node)
      .filter((node): node is NonNullable<typeof node> => node != null)
      .map(node => ({
        id: node.id,
        name: node.name,
        knownName: node.knownName ?? "",
        firstName: node.firstName,
        lastName: node.lastName,
      }));
  }, [data?.listMyOrganizationsPlayers?.edges, shouldSkip]);

  const getPlayerUuidByName = useCallback(
    (playerName: string): string | null => {
      if (!playerName || players.length === 0) {
        return null;
      }

      const normalizedSearchName = normalizeForSearch(playerName);

      const exactMatch = players.find(
        player => normalizeForSearch(player.knownName) === normalizedSearchName
      );

      if (exactMatch) {
        return exactMatch.id;
      }

      const fullNameMatch = players.find(player => {
        const fullName = normalizeForSearch(player?.name ?? "");
        return fullName === normalizedSearchName;
      });

      if (fullNameMatch) {
        return fullNameMatch.id;
      }

      const partialMatch = players.find(player => {
        // SE-5469: match the backend-canonical full name FIRST so particle
        // names ("Virgil van Dijk") resolve. The knownName/firstName/lastName
        // alias matches are kept — this resolver matches aliases, it never
        // assembles a display name from parts.
        const nameNormalized = normalizeForSearch(player.name);
        const knownNameNormalized = normalizeForSearch(player.knownName);
        const firstNameNormalized = normalizeForSearch(player.firstName);
        const lastNameNormalized = normalizeForSearch(player.lastName);

        // NOTE: `"anything".includes("")` is always true, so a bidirectional
        // match must be guarded on a non-empty candidate or every player
        // matches.
        const bidirectional = (candidate: string): boolean =>
          candidate !== "" &&
          (candidate.includes(normalizedSearchName) ||
            normalizedSearchName.includes(candidate));

        return (
          bidirectional(nameNormalized) ||
          bidirectional(knownNameNormalized) ||
          (firstNameNormalized !== "" &&
            firstNameNormalized.includes(normalizedSearchName)) ||
          (lastNameNormalized !== "" &&
            lastNameNormalized.includes(normalizedSearchName))
        );
      });

      if (partialMatch) {
        return partialMatch.id;
      }

      return null;
    },
    [players]
  );

  const getPlayerUuidFromMarkdown = useCallback(
    (markdownContent: string, sequentialId: string): string | null => {
      const targetPattern = new RegExp(
        `\\[([^\\]]+)\\]\\(\\/players\\/${sequentialId}\\)`
      );
      const targetMatch = targetPattern.exec(markdownContent);

      if (targetMatch && targetMatch[1]) {
        const playerName = targetMatch[1];
        return getPlayerUuidByName(playerName);
      }

      return null;
    },
    [getPlayerUuidByName]
  );

  return {
    getPlayerUuidByName,
    getPlayerUuidFromMarkdown,
    isLoading: shouldSkip ? false : loading,
    error: error ?? null,
  };
};
